import React, { useEffect, useState, Children } from 'react';
import { Search, SlidersHorizontal, ChevronDown, MapPin, Bed, Bath, Ruler, Heart, Plus, X, Upload, Image as ImageIcon, Check, Navigation } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
import { TopBar } from '../components/TopBar';
import { MainTabs } from '../components/MainTabs';
interface PropertiesPageProps {
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onClientsClick?: () => void;
  onDashboardClick?: () => void;
}
type PropertyType = 'rent' | 'sale';
interface Property {
  id: number;
  title: string;
  price: string;
  location: string;
  beds: number;
  baths: number;
  area: string;
  image: string;
  type: PropertyType;
  isFavorite?: boolean;
}
export function PropertiesPage({
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onClientsClick,
  onDashboardClick
}: PropertiesPageProps) {
  const [activeTab, setActiveTab] = useState<PropertyType>('sale');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<number[]>([]);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  // Add Property Form State
  const [transactionType, setTransactionType] = useState('Sale');
  const [location, setLocation] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [propertyGroup, setPropertyGroup] = useState('Residential');
  const [propertyType, setPropertyType] = useState('Apartments');
  const [propertyStage, setPropertyStage] = useState('New');
  const [propertyView, setPropertyView] = useState('');
  const [finishedType, setFinishedType] = useState('');
  const [propertySize, setPropertySize] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [inCompound, setInCompound] = useState(false);
  const [isReadyToMove, setIsReadyToMove] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState('');
  const [isInsurance, setIsInsurance] = useState(false);
  const [isInstallments, setIsInstallments] = useState(false);
  const [description, setDescription] = useState('');
  const [propertyImages, setPropertyImages] = useState<string[]>([]);
  const [selectedPropertyAmenities, setSelectedPropertyAmenities] = useState<string[]>([]);
  const [selectedCommunityAmenities, setSelectedCommunityAmenities] = useState<string[]>([]);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Mock properties data with Unsplash images
  const [properties] = useState<Property[]>([
  // Sale Properties
  {
    id: 1,
    title: 'Luxury Villa with Pool',
    price: '8,500,000 EGP',
    location: 'New Cairo, 5th Settlement',
    beds: 5,
    baths: 4,
    area: '450 sqm',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80',
    type: 'sale'
  }, {
    id: 2,
    title: 'Modern Apartment',
    price: '3,200,000 EGP',
    location: 'New Cairo, Waterway',
    beds: 3,
    baths: 2,
    area: '185 sqm',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80',
    type: 'sale'
  }, {
    id: 3,
    title: 'Penthouse with View',
    price: '12,000,000 EGP',
    location: 'New Cairo, Madinaty',
    beds: 4,
    baths: 3,
    area: '320 sqm',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80',
    type: 'sale'
  }, {
    id: 4,
    title: 'Duplex in Compound',
    price: '5,800,000 EGP',
    location: 'New Cairo, Hyde Park',
    beds: 4,
    baths: 3,
    area: '280 sqm',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
    type: 'sale'
  }, {
    id: 5,
    title: 'Townhouse with Garden',
    price: '6,500,000 EGP',
    location: 'New Cairo, Rehab',
    beds: 4,
    baths: 3,
    area: '300 sqm',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    type: 'sale'
  }, {
    id: 6,
    title: 'Studio Apartment',
    price: '1,800,000 EGP',
    location: 'New Cairo, Waterway',
    beds: 1,
    baths: 1,
    area: '65 sqm',
    image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80',
    type: 'sale'
  },
  // Rent Properties
  {
    id: 7,
    title: 'Furnished Apartment',
    price: '25,000 EGP/month',
    location: 'New Cairo, 5th Settlement',
    beds: 3,
    baths: 2,
    area: '180 sqm',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
    type: 'rent'
  }, {
    id: 8,
    title: 'Luxury Villa for Rent',
    price: '80,000 EGP/month',
    location: 'New Cairo, Hyde Park',
    beds: 5,
    baths: 4,
    area: '400 sqm',
    image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800&q=80',
    type: 'rent'
  }, {
    id: 9,
    title: 'Modern Studio',
    price: '12,000 EGP/month',
    location: 'New Cairo, Waterway',
    beds: 1,
    baths: 1,
    area: '70 sqm',
    image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80',
    type: 'rent'
  }, {
    id: 10,
    title: 'Family Apartment',
    price: '35,000 EGP/month',
    location: 'New Cairo, Madinaty',
    beds: 3,
    baths: 2,
    area: '200 sqm',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80',
    type: 'rent'
  }, {
    id: 11,
    title: 'Penthouse Rental',
    price: '60,000 EGP/month',
    location: 'New Cairo, Waterway',
    beds: 4,
    baths: 3,
    area: '280 sqm',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
    type: 'rent'
  }, {
    id: 12,
    title: 'Cozy 2BR Apartment',
    price: '18,000 EGP/month',
    location: 'New Cairo, 5th Settlement',
    beds: 2,
    baths: 2,
    area: '120 sqm',
    image: 'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?w=800&q=80',
    type: 'rent'
  }]);
  const toggleFavorite = (propertyId: number) => {
    setFavorites(prev => prev.includes(propertyId) ? prev.filter(id => id !== propertyId) : [...prev, propertyId]);
  };
  const handleAddProperty = () => {
    setShowAddPropertyModal(true);
  };
  const handleCloseAddProperty = () => {
    setShowAddPropertyModal(false);
    // Reset form
    setTransactionType('Sale');
    setLocation('');
    setPropertyGroup('Residential');
    setPropertyType('Apartments');
    setPropertyStage('New');
    setPropertyView('');
    setFinishedType('');
    setPropertySize('');
    setBeds('');
    setBaths('');
    setInCompound(false);
    setIsReadyToMove(false);
    setEstimatedPrice('');
    setIsInsurance(false);
    setIsInstallments(false);
    setDescription('');
    setPropertyImages([]);
    setSelectedPropertyAmenities([]);
    setSelectedCommunityAmenities([]);
  };
  const handleSubmitProperty = () => {
    alert('Property added successfully!');
    handleCloseAddProperty();
  };
  const togglePropertyAmenity = (amenity: string) => {
    setSelectedPropertyAmenities(prev => prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]);
  };
  const toggleCommunityAmenity = (amenity: string) => {
    setSelectedCommunityAmenities(prev => prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]);
  };
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(async position => {
      const {
        latitude,
        longitude
      } = position.coords;
      // Use reverse geocoding to get address (using a free API)
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
        const data = await response.json();
        // Extract relevant location info
        const locationName = data.address.suburb || data.address.city || data.address.town || data.address.village || 'Current Location';
        setLocation(locationName);
      } catch (error) {
        // If reverse geocoding fails, just show coordinates
        setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      }
      setIsLoadingLocation(false);
    }, error => {
      setIsLoadingLocation(false);
      switch (error.code) {
        case error.PERMISSION_DENIED:
          alert('Location access denied. Please enable location permissions in your browser settings.');
          break;
        case error.POSITION_UNAVAILABLE:
          alert('Location information is unavailable.');
          break;
        case error.TIMEOUT:
          alert('Location request timed out.');
          break;
        default:
          alert('An error occurred while getting your location.');
      }
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };
  const propertyAmenities = ['Balcony/Terrace', "Maid's Room", 'Central Air Conditioning', 'Private Pool', 'Built-in Wardrobes', 'Fully Fitted Kitchen', 'Furnished/Unfurnished', 'High-Speed Internet', 'Smart Home Technology', 'Pet-Friendly'];
  const communityAmenities = ['Gymnasium', 'Shared Swimming Pool', "Children's Play Area", 'BBQ Area', '24/7 Security', 'Covered Parking', 'Landscaped Gardens', 'Tennis/Squash Courts', 'Sauna/Steam Room', 'Retail Shops', 'Jogging/Cycling Tracks', 'Community Hall', 'Public Transport Access', 'Sports Area', 'Beach Hotel', 'Beach Club', 'Boutique Hotel', 'Sports Clubs', 'Schools', 'Hospital', 'Retail', 'Infrastructure', 'Lagoon', 'Smart Home Technology', 'Furnished/Unfurnished', 'Built-in Wardrobes', 'High-Speed Internet', 'Private Pool', "Maid's Room", 'Balcony/Terrace', 'Pet-Friendly', 'Central Air Conditioning', 'Fully Fitted Kitchen'];
  const filteredProperties = properties.filter(property => property.type === activeTab && (property.title.toLowerCase().includes(searchQuery.toLowerCase()) || property.location.toLowerCase().includes(searchQuery.toLowerCase())));
  const handleTabChange = (tab: 'home' | 'clients' | 'properties') => {
    if (tab === 'home' && onDashboardClick) {
      onDashboardClick();
    } else if (tab === 'clients' && onClientsClick) {
      onClientsClick();
    }
    // 'properties' tab is already active, no action needed
  };
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-1578">
      <div className="w-full mx-auto bg-white min-h-screen shadow-2xl relative" data-id="element-1579">
        <TopBar data-id="element-1580" />
        <MainTabs activeTab="properties" onTabChange={handleTabChange} data-id="element-1581" />

        {/* Add Property Button */}
        <div className="px-4 pt-4 pb-2" data-id="element-1582">
          <button onClick={handleAddProperty} className="w-full bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary text-white font-bold py-3 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-1583">
            <Plus className="w-5 h-5" data-id="element-1584" />
            Add New Property
          </button>
        </div>

        {/* Search and Filters */}
        <div className="px-4 pt-4 pb-3 bg-white sticky top-[120px] z-10 border-b border-gray-100" data-id="element-1585">
          <div className="relative mb-3" data-id="element-1586">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1587" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search properties or location" className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary shadow-sm" data-id="element-1588" />
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-id="element-1589">
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 whitespace-nowrap hover:border-gray-300" data-id="element-1590">
              <SlidersHorizontal className="w-3.5 h-3.5" data-id="element-1591" />
              All Filters
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 whitespace-nowrap hover:border-gray-300" data-id="element-1592">
              Price <ChevronDown className="w-3.5 h-3.5 text-gray-400" data-id="element-1593" />
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 whitespace-nowrap hover:border-gray-300" data-id="element-1594">
              Beds <ChevronDown className="w-3.5 h-3.5 text-gray-400" data-id="element-1595" />
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 whitespace-nowrap hover:border-gray-300" data-id="element-1596">
              Type <ChevronDown className="w-3.5 h-3.5 text-gray-400" data-id="element-1597" />
            </button>
            <button className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 whitespace-nowrap hover:border-gray-300" data-id="element-1598">
              Area <ChevronDown className="w-3.5 h-3.5 text-gray-400" data-id="element-1599" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-3 pb-3 bg-white" data-id="element-1600">
          <div className="flex gap-3" data-id="element-1601">
            <button onClick={() => setActiveTab('sale')} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeTab === 'sale' ? 'bg-secondary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-1602">
              For Sale ({properties.filter(p => p.type === 'sale').length})
            </button>
            <button onClick={() => setActiveTab('rent')} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${activeTab === 'rent' ? 'bg-secondary text-white' : 'bg-white text-gray-600 border border-gray-200'}`} data-id="element-1603">
              For Rent ({properties.filter(p => p.type === 'rent').length})
            </button>
          </div>
        </div>

        {/* Properties Grid */}
        <div className="px-4 pb-4" data-id="element-1604">
          <div className="grid grid-cols-1 gap-4" data-id="element-1605">
            {filteredProperties.map(property => <div key={property.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1606">
                {/* Property Image */}
                <div className="relative h-48" data-id="element-1607">
                  <img src={property.image} alt={property.title} className="w-full h-full object-cover" data-id="element-1608" />
                  <button onClick={() => toggleFavorite(property.id)} className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors" data-id="element-1609">
                    <Heart className={`w-4 h-4 ${favorites.includes(property.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} data-id="element-1610" />
                  </button>
                  <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white text-sm font-semibold px-3 py-1.5 rounded-lg" data-id="element-1611">
                    {property.price}
                  </div>
                </div>

                {/* Property Info */}
                <div className="p-4" data-id="element-1612">
                  <h3 className="font-bold text-neutral-text text-base mb-2" data-id="element-1613">
                    {property.title}
                  </h3>

                  <div className="flex items-center text-gray-500 text-sm mb-3" data-id="element-1614">
                    <MapPin className="w-4 h-4 mr-1 flex-shrink-0" data-id="element-1615" />
                    <span className="line-clamp-1" data-id="element-1616">{property.location}</span>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-1617">
                    <div className="flex items-center gap-1 text-gray-600 text-sm" data-id="element-1618">
                      <Bed className="w-4 h-4 text-gray-400" data-id="element-1619" />
                      <span data-id="element-1620">{property.beds}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600 text-sm" data-id="element-1621">
                      <Bath className="w-4 h-4 text-gray-400" data-id="element-1622" />
                      <span data-id="element-1623">{property.baths}</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600 text-sm" data-id="element-1624">
                      <Ruler className="w-4 h-4 text-gray-400" data-id="element-1625" />
                      <span data-id="element-1626">{property.area}</span>
                    </div>
                    <button className="text-sm font-semibold text-secondary hover:text-secondary-dark transition-colors" data-id="element-1627">
                      View Details
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Add Property Modal */}
        {showAddPropertyModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-1628">
            <div className="bg-white w-full sm:max-w-4xl rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-1629">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-1630">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-1631">
                  Add New Property
                </h2>
                <button onClick={handleCloseAddProperty} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-1632">
                  <X className="w-5 h-5" data-id="element-1633" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1634">
                {/* Transaction Type - Radio Buttons */}
                <div data-id="element-1635">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1636">
                    Transaction Type
                  </label>
                  <div className="flex gap-4" data-id="element-1637">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-1638">
                      <input type="radio" name="transactionType" value="Sale" checked={transactionType === 'Sale'} onChange={e => setTransactionType(e.target.value)} className="w-5 h-5 text-secondary border-gray-300 focus:ring-secondary" data-id="element-1639" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-1640">
                        Sale
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-1641">
                      <input type="radio" name="transactionType" value="Rent" checked={transactionType === 'Rent'} onChange={e => setTransactionType(e.target.value)} className="w-5 h-5 text-secondary border-gray-300 focus:ring-secondary" data-id="element-1642" />
                      <span className="text-sm font-medium text-gray-700" data-id="element-1643">
                        Rent
                      </span>
                    </label>
                  </div>
                </div>

                {/* Location - Search Input */}
                <div data-id="element-1644">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1645">
                    Select Location
                  </label>
                  <div className="space-y-2" data-id="element-1646">
                    <div className="relative" data-id="element-1647">
                      <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1648" />
                      <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Search location..." className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-1649" />
                    </div>
                    <button type="button" onClick={handleUseCurrentLocation} disabled={isLoadingLocation} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1650">
                      <Navigation className={`w-4 h-4 ${isLoadingLocation ? 'animate-spin' : ''}`} data-id="element-1651" />
                      {isLoadingLocation ? 'Getting location...' : 'Use Current Location'}
                    </button>
                  </div>
                </div>

                {/* 2 Fields Per Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6" data-id="element-1652">
                  {/* Property Group */}
                  <div data-id="element-1653">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1654">
                      Property Group
                    </label>
                    <select value={propertyGroup} onChange={e => setPropertyGroup(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-1655">
                      <option value="Residential" data-id="element-1656">Residential</option>
                      <option value="Commercial" data-id="element-1657">Commercial</option>
                      <option value="Administrative" data-id="element-1658">Administrative</option>
                    </select>
                  </div>

                  {/* Property Type */}
                  <div data-id="element-1659">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1660">
                      Property Type
                    </label>
                    <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-1661">
                      <option value="Apartments" data-id="element-1662">Apartments</option>
                      <option value="Villa" data-id="element-1663">Villa</option>
                      <option value="Townhouse" data-id="element-1664">Townhouse</option>
                      <option value="Penthouse" data-id="element-1665">Penthouse</option>
                      <option value="Duplex" data-id="element-1666">Duplex</option>
                      <option value="Studio" data-id="element-1667">Studio</option>
                    </select>
                  </div>

                  {/* Property View */}
                  <div data-id="element-1668">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1669">
                      Property View
                    </label>
                    <select value={propertyView} onChange={e => setPropertyView(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-1670">
                      <option value="" data-id="element-1671">Select View</option>
                      <option value="Garden View" data-id="element-1672">Garden View</option>
                      <option value="Pool View" data-id="element-1673">Pool View</option>
                      <option value="Street View" data-id="element-1674">Street View</option>
                      <option value="Landscape View" data-id="element-1675">Landscape View</option>
                      <option value="Golf View" data-id="element-1676">Golf View</option>
                    </select>
                  </div>

                  {/* Finished Type */}
                  <div data-id="element-1677">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1678">
                      Finished Type
                    </label>
                    <select value={finishedType} onChange={e => setFinishedType(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-1679">
                      <option value="" data-id="element-1680">Select Type</option>
                      <option value="Fully Finished" data-id="element-1681">Fully Finished</option>
                      <option value="Semi Finished" data-id="element-1682">Semi Finished</option>
                      <option value="Core & Shell" data-id="element-1683">Core & Shell</option>
                      <option value="Furnished" data-id="element-1684">Furnished</option>
                    </select>
                  </div>

                  {/* Property Size */}
                  <div data-id="element-1685">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1686">
                      Property Size
                    </label>
                    <div className="relative" data-id="element-1687">
                      <input type="number" value={propertySize} onChange={e => setPropertySize(e.target.value)} placeholder="Enter size" className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-1688" />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm" data-id="element-1689">
                        m²
                      </span>
                    </div>
                  </div>

                  {/* Beds */}
                  <div data-id="element-1690">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1691">
                      Beds
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-1692">
                      {['Studio', '1', '2', '3', '4', '5', '6+'].map(bedOption => <button key={bedOption} type="button" onClick={() => setBeds(bedOption)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all border-2 min-w-[60px] ${beds === bedOption ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-1693">
                            {bedOption}
                          </button>)}
                    </div>
                  </div>

                  {/* Baths */}
                  <div data-id="element-1694">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1695">
                      Baths
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-1696">
                      {['1', '2', '3', '4', '5', '6+'].map(bathOption => <button key={bathOption} type="button" onClick={() => setBaths(bathOption)} className={`px-4 py-2.5 rounded-lg font-semibold text-sm transition-all border-2 min-w-[60px] ${baths === bathOption ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-1697">
                          {bathOption}
                        </button>)}
                    </div>
                  </div>
                </div>

                {/* Property Features */}
                <div data-id="element-1698">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-1699">
                    Property Features
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1700">
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-1701">
                      <input type="checkbox" checked={inCompound} onChange={e => setInCompound(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-1702" />
                      <span className="text-sm text-gray-700" data-id="element-1703">In Compound</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer" data-id="element-1704">
                      <input type="checkbox" checked={isReadyToMove} onChange={e => setIsReadyToMove(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-1705" />
                      <span className="text-sm text-gray-700" data-id="element-1706">
                        Is Ready To Move
                      </span>
                    </label>
                  </div>
                </div>

                {/* Estimated Price */}
                <div data-id="element-1707">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-1708">
                    Estimated Price
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1709">
                    <div className="md:col-span-1" data-id="element-1710">
                      <div className="relative" data-id="element-1711">
                        <input type="text" value={estimatedPrice} onChange={e => setEstimatedPrice(e.target.value)} placeholder="Estimated Price" className="w-full px-4 py-3 pr-16 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-1712" />
                        <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-semibold" data-id="element-1713">
                          EGP
                        </span>
                      </div>
                    </div>
                    <div className="md:col-span-2 flex gap-4" data-id="element-1714">
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-1715">
                        <input type="checkbox" checked={isInsurance} onChange={e => setIsInsurance(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-1716" />
                        <span className="text-sm text-gray-700" data-id="element-1717">
                          Is Insurance
                        </span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer" data-id="element-1718">
                        <input type="checkbox" checked={isInstallments} onChange={e => setIsInstallments(e.target.checked)} className="w-5 h-5 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-1719" />
                        <span className="text-sm text-gray-700" data-id="element-1720">
                          Is Installments
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div data-id="element-1721">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-1722">
                    Description
                  </h3>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Enter property description..." rows={6} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none" data-id="element-1723" />
                </div>

                {/* Property Images */}
                <div data-id="element-1724">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-1725">
                    Property Images
                  </h3>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-secondary transition-colors cursor-pointer" data-id="element-1726">
                    <div className="flex flex-col items-center gap-3" data-id="element-1727">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1728">
                        <ImageIcon className="w-8 h-8 text-gray-400" data-id="element-1729" />
                      </div>
                      <div data-id="element-1730">
                        <p className="text-base font-semibold text-neutral-text mb-1" data-id="element-1731">
                          Click to upload
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-1732">
                          or drag and drop
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Property Amenities */}
                <div data-id="element-1733">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-1734">
                    Property Amenities
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1735">
                    {propertyAmenities.map(amenity => <label key={amenity} className="flex items-center gap-2 cursor-pointer" data-id="element-1736">
                        <input type="checkbox" checked={selectedPropertyAmenities.includes(amenity)} onChange={() => togglePropertyAmenity(amenity)} className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-1737" />
                        <span className="text-sm text-gray-700" data-id="element-1738">{amenity}</span>
                      </label>)}
                  </div>
                </div>

                {/* Community Amenities */}
                <div data-id="element-1739">
                  <h3 className="text-base font-bold text-neutral-text mb-3" data-id="element-1740">
                    Community Amenities
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1741">
                    {communityAmenities.map(amenity => <label key={amenity} className="flex items-center gap-2 cursor-pointer" data-id="element-1742">
                        <input type="checkbox" checked={selectedCommunityAmenities.includes(amenity)} onChange={() => toggleCommunityAmenity(amenity)} className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-secondary" data-id="element-1743" />
                        <span className="text-sm text-gray-700" data-id="element-1744">{amenity}</span>
                      </label>)}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-1745">
                <div className="flex gap-3" data-id="element-1746">
                  <button onClick={handleCloseAddProperty} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-4 rounded-xl transition-colors" data-id="element-1747">
                    Cancel
                  </button>
                  <button onClick={handleSubmitProperty} disabled={!location || !propertySize || !estimatedPrice} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1748">
                    Save Property
                  </button>
                </div>
              </div>
            </div>
          </div>}

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} data-id="element-1749" />
      </div>
    </div>;
}